'use client'

import { useState } from 'react'
import { Lock } from 'lucide-react'
import type { DragEvent } from 'react'
import type { Task, Sprint } from '@/lib/types'
import { taskInSprint, riskFlags, kanbanColumn, KANBAN_COLS, type KanbanCol, type RiskCtx } from '@/lib/dev-sprint'
import { useToast } from '@/context/ToastContext'
import type { BoardData, Filters, DevDerived } from './types'
import { taskMatchesFilters } from './filter'
import { TaskCard } from './TaskCard'
import { Avatar } from './ui'

const UNASSIGNED = '__unassigned__'
const norm = (x?: string | null) => (x ?? '').trim().toLowerCase()
const COL_DOT: Record<KanbanCol, string> = { todo: '#B9B8B2', inprogress: '#E0A32E', review: '#7A5AF8', done: '#34C77B' }

interface Props {
  board: BoardData
  sprint: Sprint | null
  derived: DevDerived
  riskCtx: RiskCtx
  filters: Filters
  canEditTaskFn: (assignee: string | null) => boolean
  isLead: boolean
  readOnly: boolean
  onOpenTask: (id: string) => void
  onChanged: () => void
}

export function KanbanView({ board, sprint, derived, riskCtx, filters, canEditTaskFn, isLead, readOnly, onOpenTask, onChanged }: Props) {
  const { toast } = useToast()
  const [dragId, setDragId] = useState<string | null>(null)
  const [overKey, setOverKey] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [scope, setScope] = useState<'sprint' | 'all'>(sprint ? 'sprint' : 'all')

  const isAuto = (taskId: string) => (derived.subsByTask.get(taskId) ?? []).some((s) => !s.recurrence)
  const inScope = (t: Task) => (scope === 'all' || !sprint ? true : taskInSprint(t, sprint))

  const rows: { key: string; email: string; name: string; username: string | null }[] = board.team.map((m) => ({
    key: norm(m.username), email: m.email, name: m.name, username: m.username,
  }))
  const cells = new Map<string, Task[]>()
  const push = (k: string, t: Task) => { const a = cells.get(k) ?? []; a.push(t); cells.set(k, a) }
  let hasUnassigned = false
  for (const t of board.tasks) {
    if (!inScope(t)) continue
    if (!taskMatchesFilters(t, filters, derived, riskCtx)) continue
    const devKey = norm(t.assignee) || UNASSIGNED
    if (devKey === UNASSIGNED) hasUnassigned = true
    push(`${devKey}::${kanbanColumn(t, derived.statusOf(t))}`, t)
  }
  const allRows = hasUnassigned ? [...rows, { key: UNASSIGNED, email: UNASSIGNED, name: 'Belum di-assign', username: null }] : rows

  const drop = async (targetUsername: string | null, col: KanbanCol) => {
    const id = dragId
    setDragId(null); setOverKey(null)
    if (!id || busy || readOnly) return
    const t = board.tasks.find((x) => x.id === id)
    if (!t) return
    if (!canEditTaskFn(t.assignee)) { toast('Tidak berwenang mengubah task ini', 'error'); return }
    const changingDev = norm(targetUsername) !== norm(t.assignee)
    if (changingDev && !isLead) { toast('Hanya lead yang bisa pindahkan ke dev lain', 'info'); return }
    const auto = isAuto(t.id)
    const curCol = kanbanColumn(t, derived.statusOf(t))
    const colChanged = curCol !== col
    const reassign = changingDev && isLead
    if (!colChanged && !reassign) return

    const body: Record<string, unknown> = {}
    let blockedByAuto = false
    if (colChanged) {
      const eff = derived.statusOf(t)
      if (col === 'review') {
        // Task auto yang sudah done tak bisa direview (statusnya dari subtask).
        if (auto && eff === 'done') blockedByAuto = true
        else { body.inReview = true; if (!auto && eff !== 'in-progress') body.status = 'in-progress' }
      } else if (col === 'inprogress') {
        if (auto && eff === 'done') blockedByAuto = true
        else { body.inReview = false; if (!auto) body.status = 'in-progress' }
      } else {
        // todo / done → ubah status; task auto (dari subtask) tak bisa
        if (auto) blockedByAuto = true
        else { body.inReview = false; body.status = col === 'done' ? 'done' : 'not-started' }
      }
    }
    if (reassign) body.assignee = targetUsername || null
    // Perubahan kolom diblok status-otomatis & tak ada reassign → beri tahu, jangan diam.
    if (blockedByAuto && !('assignee' in body)) { toast('Status task ini otomatis dari subtask', 'info'); return }
    if (Object.keys(body).length === 0) return

    setBusy(true)
    try {
      const res = await fetch(`/api/dev/tasks/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Gagal')
      onChanged()
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Gagal', 'error')
    } finally { setBusy(false) }
  }

  const onDragStart = (t: Task) => (e: DragEvent) => {
    if (readOnly || !canEditTaskFn(t.assignee)) { e.preventDefault(); return }
    setDragId(t.id)
    e.dataTransfer.effectAllowed = 'move'
  }

  const gridCols = '160px repeat(4, minmax(200px, 1fr))'

  return (
    <div className="rounded-2xl border border-[#E7E5DF] bg-white">
      {/* Scope toggle */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#EDEBE5]">
        <span className="text-[12px] font-semibold text-[#8B8A85]">Tampilkan:</span>
        <div className="inline-flex rounded-lg border border-[#E7E5DF] p-0.5 bg-[#F7F6F2]">
          {(['sprint', 'all'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              disabled={s === 'sprint' && !sprint}
              className={`px-2.5 py-1 rounded-md text-[12px] font-semibold transition-colors disabled:opacity-40 ${scope === s ? 'bg-white text-[#1A1916] shadow-sm' : 'text-[#8B8A85] hover:text-[#1A1916]'}`}
            >
              {s === 'sprint' ? 'Sprint ini' : 'Semua task'}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div style={{ display: 'grid', gridTemplateColumns: gridCols }} className="min-w-max">
          {/* Header */}
          <div className="sticky left-0 z-10 bg-[#FAF9F6] border-b border-r border-[#E7E5DF] px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide text-[#8B8A85]">Dev</div>
          {KANBAN_COLS.map((c) => (
            <div key={c.key} className="border-b border-[#E7E5DF] px-3 py-2.5 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COL_DOT[c.key] }} />
              <span className="text-[12px] font-bold text-[#1A1916]">{c.label}</span>
            </div>
          ))}

          {/* Rows */}
          {allRows.map((row) => (
            <div key={row.email} className="contents">
              <div className="sticky left-0 z-10 bg-white border-b border-r border-[#E7E5DF] px-3 py-3 flex items-center gap-2">
                {row.username ? <Avatar name={row.name} size={26} /> : <span className="w-[26px] h-[26px] rounded-full bg-[#F0EEE7] flex-shrink-0" />}
                <span className="text-[13px] font-semibold text-[#1A1916] truncate">{row.name}</span>
              </div>
              {KANBAN_COLS.map((c) => {
                const key = `${row.key}::${c.key}`
                const list = cells.get(key) ?? []
                return (
                  <div
                    key={c.key}
                    onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverKey(key) } }}
                    onDragLeave={() => setOverKey((k) => (k === key ? null : k))}
                    onDrop={(e) => { e.preventDefault(); drop(row.username, c.key) }}
                    className={`border-b border-[#EDEBE5] p-2 space-y-2 min-h-[92px] transition-colors ${overKey === key ? 'bg-[#F3EEFF]' : ''}`}
                  >
                    {list.map((t) => {
                      const p = derived.projById.get(t.projectId)
                      const draggable = !readOnly && canEditTaskFn(t.assignee)
                      return (
                        <div key={t.id} className="relative">
                          <TaskCard
                            task={t}
                            projectName={p?.name ?? '—'}
                            projectColor={p?.color ?? '#D4A843'}
                            status={derived.statusOf(t)}
                            risks={riskFlags(t, riskCtx)}
                            showStatus={false}
                            onClick={() => onOpenTask(t.id)}
                            draggable={draggable}
                            onDragStart={onDragStart(t)}
                            onDragEnd={() => { setDragId(null); setOverKey(null) }}
                          />
                          {isAuto(t.id) && (
                            <span className="absolute top-2 right-2 text-[#C9C7C0]" title="Status otomatis dari subtask"><Lock className="w-3 h-3" /></span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
